// The race timeline: a buffer of stamped frames that can say what the race looked like at any
// buffered moment. The game renders it 2 s behind the newest frame (see playout.ts), so every
// rendered moment lies between frames it already has.
//
// Each cat has two positions:
// - signalPos: the frames' own value, linearly interpolated. The truth for standings and the race map.
// - displayPos: a centred Gaussian smoothing of the same frames (σ 250 ms, window ±600 ms), kept within
//   0.004 of signalPos and never decreasing within a segment. It places the 3D cat; its derivative is speed.
//   Within 1 s of a surge (a cat snapped forward onto the ham) the allowance grows to the surge's size.
// Segments split at every discontinuity (race start, catnip landing, a jump across a gap), so neither
// value is ever blended across a teleport: at a landing both step exactly at the landing frame.
import { TRACK_LENGTH } from '../data/track';
import { SignalClock } from '../signal/clock';
import { EventDeriver } from './events';
import { officialResults, standings } from './standings';
import {
  CAT_IDS, type CatSnapshot, type Phase, type RaceEvent, type RaceSnapshot, type RaceTimeline,
  type SignalFrame, type StampedFrame,
} from './types';

export const SIGMA_MS = 250;
export const WINDOW_MS = 600;
export const MAX_DEVIATION = 0.004;
/** A forward step per frame larger than this is a surge (the site snaps a cat onto the ham it reaches). */
export const SURGE_STEP = 0.004;
/** Around a surge, displayPos may differ from signalPos by up to the surge's size, so the 3D cat
 *  bounds onto the ham over about a second instead of dashing at 70 m/s. */
export const SURGE_WINDOW_MS = 1500;
const SURGE_MAX = 0.05;
/** Near a surge the smoothing widens so the bound spreads over about 1.5 s. */
const SURGE_SIGMA_MS = 500;
/** At a segment's start (the gate, a landing) the cat may not run ahead of the signal yet;
 *  the lead it may take grows by this much per ms. */
const AHEAD_GROWTH_PER_MS = 0.00002;
export const PEEK_MAX_MS = 2000;
/** displayPos looks this far ahead so it never has to back up when the signal steps back a little. */
const LOOKAHEAD_MS = 1200;
/** A pause longer than this across a phase or race change makes the playout clock jump past it. */
const PHASE_GAP_MS = 1500;
const SPEED_SPAN_MS = 100;

type Health = RaceSnapshot['signal'];
type Source = RaceSnapshot['source'];

interface CatState { pos: number; ham: boolean; nip: boolean }

interface Rec extends StampedFrame {
  catsNow: CatState[]; // running: the frame's cats; starting: all at 0; finished: carried from the last running frame
  seg: number[]; // per lane: smoothing segment id
  segStart: number[]; // per lane: signal time the segment began
  finishPlace: (number | undefined)[];
  phaseStart: number;
  disp?: number[]; // finalized displayPos per lane
}

export interface TimelineOptions {
  source: Source;
  /** How much history to keep, ms. */
  keepMs?: number;
}

export class Timeline implements RaceTimeline {
  readonly source: Source;
  private readonly keepMs: number;
  private readonly clock = new SignalClock();
  private readonly deriver = new EventDeriver();
  private recs: Rec[] = [];
  private events: RaceEvent[] = [];
  private gaps: { from: number; to: number }[] = [];
  private finalized = 0; // recs[0 .. finalized) have disp
  private segCounter = 0;
  private health: Health = 'ok';

  constructor(opts: TimelineOptions) {
    this.source = opts.source;
    this.keepMs = opts.keepMs ?? 60_000;
  }

  // ---- input ---------------------------------------------------------------------------------------

  push(frame: SignalFrame, arrivalMs: number): void {
    const f = this.clock.stamp(frame, arrivalMs);
    const facts = this.deriver.push(f);
    const prev = this.recs[this.recs.length - 1];
    const sameRun = prev?.state === f.state && prev.raceNo === f.raceNo;

    let catsNow: CatState[];
    if (f.state === 'running' && f.cats) catsNow = f.cats.map((c) => ({ ...c }));
    else if (f.state === 'starting') catsNow = CAT_IDS.map(() => ({ pos: 0, ham: false, nip: false }));
    else {
      // The race is over: every cat with a time has crossed the line.
      catsNow = CAT_IDS.map(() => ({ pos: 1, ham: false, nip: false }));
    }

    const longGap = prev ? f.t - prev.t : 0;
    const seg = CAT_IDS.map((_, lane) => {
      if (!prev || !sameRun || facts.breaks[lane] || longGap > 6000) return ++this.segCounter;
      return prev.seg[lane]!;
    });

    const segStart = seg.map((id, lane) => (prev && prev.seg[lane] === id ? prev.segStart[lane]! : f.t));

    if (prev && !sameRun && longGap > PHASE_GAP_MS) this.gaps.push({ from: prev.t, to: f.t });

    this.recs.push({
      ...f, catsNow, seg, segStart, finishPlace: facts.finishPlace,
      phaseStart: sameRun ? prev.phaseStart : f.t,
    });
    for (const e of facts.events) this.addEvent(e);
    this.trim();
  }

  /** Adds an event from outside the signal, such as a change of buffer health or source. */
  addEvent(e: RaceEvent): void {
    let i = this.events.length;
    while (i > 0 && this.events[i - 1]!.t > e.t) i--;
    this.events.splice(i, 0, e);
  }

  setHealth(h: Health): void {
    this.health = h;
  }

  // ---- queries --------------------------------------------------------------------------------------

  newestT(): number {
    return this.recs.length ? this.recs[this.recs.length - 1]!.t : Number.NaN;
  }

  oldestT(): number {
    return this.recs.length ? this.recs[0]!.t : Number.NaN;
  }

  /** How often frames arrive in the current phase: 100 ms while running, once a second otherwise. */
  cadenceMs(): number {
    return this.recs[this.recs.length - 1]?.state === 'running' ? 100 : 1000;
  }

  /** If render time sits inside a gap across which the phase or race changed, where to jump to. */
  jumpTarget(renderT: number): number | null {
    for (const g of this.gaps) if (renderT >= g.from && renderT < g.to) return g.to;
    return null;
  }

  eventsBetween(t0: number, t1: number): RaceEvent[] {
    if (!(t1 > t0)) return [];
    const out: RaceEvent[] = [];
    for (let i = this.firstEventAfter(t0); i < this.events.length; i++) {
      const e = this.events[i]!;
      if (e.t > t1) break;
      out.push(e);
    }
    return out;
  }

  peek(renderT: number, horizonMs: number): RaceEvent[] {
    return this.eventsBetween(renderT, renderT + Math.min(PEEK_MAX_MS, Math.max(0, horizonMs)));
  }

  /** All buffered frames, for tests and diagnostics. */
  frames(): readonly StampedFrame[] {
    return this.recs;
  }

  sample(renderT: number): RaceSnapshot {
    if (!this.recs.length) return this.emptySnapshot(renderT);
    const ai = this.indexAt(renderT);
    const a = this.recs[ai]!;
    const phase: Phase = a.state;

    const cats: CatSnapshot[] = CAT_IDS.map((id, lane) => {
      const here = this.catAt(ai, renderT, lane);
      const flags = a.catsNow[lane]!;
      const finishPlace = a.finishPlace[lane];
      let speed = 0;
      if (phase === 'running' && !flags.nip) {
        const back = this.catAt(this.indexAt(renderT - SPEED_SPAN_MS), renderT - SPEED_SPAN_MS, lane);
        const ahead = this.catAt(this.indexAt(renderT + SPEED_SPAN_MS), renderT + SPEED_SPAN_MS, lane);
        const p0 = back.seg === here.seg ? back : here;
        const p1 = ahead.seg === here.seg ? ahead : here;
        const dt = (p1.t - p0.t) / 1000;
        if (dt > 0) speed = Math.max(0, ((p1.disp - p0.disp) * TRACK_LENGTH) / dt);
      }
      return {
        id, lane,
        signalPos: here.sig,
        displayPos: here.disp,
        speed,
        eating: phase === 'running' && flags.ham,
        tripping: phase === 'running' && flags.nip,
        finished: finishPlace !== undefined || phase === 'finished',
        ...(finishPlace !== undefined ? { finishPlace } : {}),
        place: 0,
        gapToLeader: 0,
      };
    });
    standings(cats).forEach((s, i) => { cats[i]!.place = s.place; cats[i]!.gapToLeader = s.gapToLeader; });

    const snap: RaceSnapshot = {
      t: renderT,
      source: this.source,
      raceNo: a.raceNo,
      phase,
      phaseElapsedMs: Math.max(0, renderT - a.phaseStart),
      joinedMidRace: this.clock.joinedMidRace(a.raceNo),
      cats,
      ham: phase === 'running' ? (a.ham ?? null) : null,
      catnip: phase === 'running' ? (a.catnip ?? null) : null,
      signal: this.health,
    };
    if (phase === 'finished' && a.times) {
      // Once the official results are in, places follow them, even if the finish crossings were never seen.
      const results = officialResults(a.times);
      snap.results = results;
      for (const c of cats) {
        const r = results.find((x) => x.id === c.id);
        if (r) { c.place = r.place; c.gapToLeader = 0; }
      }
    }
    return snap;
  }

  // ---- internals ----------------------------------------------------------------------------------

  private emptySnapshot(renderT: number): RaceSnapshot {
    return {
      t: renderT, source: this.source, raceNo: 0, phase: 'starting', phaseElapsedMs: 0, joinedMidRace: false,
      cats: CAT_IDS.map((id, lane) => ({
        id, lane, signalPos: 0, displayPos: 0, speed: 0, eating: false, tripping: false,
        finished: false, place: lane + 1, gapToLeader: 0,
      })),
      ham: null, catnip: null, signal: this.health === 'ok' ? 'thin' : this.health,
    };
  }

  /** Index of the last frame at or before t (0 when t is before the first frame). */
  private indexAt(t: number): number {
    let lo = 0, hi = this.recs.length - 1;
    if (t < this.recs[0]!.t) return 0;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (this.recs[mid]!.t <= t) lo = mid; else hi = mid - 1;
    }
    return lo;
  }

  private firstEventAfter(t: number): number {
    let lo = 0, hi = this.events.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (this.events[mid]!.t <= t) lo = mid + 1; else hi = mid;
    }
    return lo;
  }

  /** A cat's signal and display position at time t, given the frame index at or before t. */
  private catAt(ai: number, t: number, lane: number): { sig: number; disp: number; seg: number; t: number } {
    const a = this.recs[ai]!;
    const b = this.recs[ai + 1];
    const da = this.dispAt(ai)[lane]!;
    const pa = a.catsNow[lane]!.pos;
    const seg = a.seg[lane]!;
    if (t <= a.t || !b || b.seg[lane] !== seg || a.state !== 'running') return { sig: pa, disp: da, seg, t: Math.max(t, a.t) };
    const u = Math.min(1, (t - a.t) / (b.t - a.t));
    const db = this.dispAt(ai + 1)[lane]!;
    const pb = b.catsNow[lane]!.pos;
    return { sig: pa + (pb - pa) * u, disp: da + (db - da) * u, seg, t };
  }

  private dispAt(i: number): number[] {
    this.finalize();
    if (i < this.finalized) return this.recs[i]!.disp!;
    // Not enough frames after it yet (the buffer is thin): compute without keeping the result.
    let prev = this.finalized > 0 ? this.recs[this.finalized - 1]!.disp! : null;
    for (let k = this.finalized; k <= i; k++) prev = this.computeDisp(k, prev);
    return prev!;
  }

  private finalize(): void {
    const newest = this.newestT();
    while (this.finalized < this.recs.length) {
      const r = this.recs[this.finalized]!;
      const settled = r.state !== 'running' || newest >= r.t + LOOKAHEAD_MS
        || this.recs[this.recs.length - 1]!.raceNo !== r.raceNo || this.recs[this.recs.length - 1]!.state !== 'running';
      if (!settled) break;
      r.disp = this.computeDisp(this.finalized, this.finalized > 0 ? this.recs[this.finalized - 1]!.disp! : null);
      this.finalized++;
    }
  }

  private computeDisp(i: number, prevDisp: number[] | null): number[] {
    const r = this.recs[i]!;
    if (r.state !== 'running') return r.catsNow.map((c) => c.pos);
    const prevRec = this.recs[i - 1];
    return r.catsNow.map((c, lane) => {
      const pos = c.pos;
      if (pos >= 1) return 1;
      const seg = r.seg[lane]!;
      const dev = this.allowance(i, lane);
      const sigma = dev > MAX_DEVIATION ? SURGE_SIGMA_MS : SIGMA_MS;
      const win = dev > MAX_DEVIATION ? 2.4 * SURGE_SIGMA_MS : WINDOW_MS;
      const devAhead = Math.min(dev, MAX_DEVIATION + AHEAD_GROWTH_PER_MS * (r.t - r.segStart[lane]!));
      let sw = 0, sp = 0;
      let upper = pos + devAhead;
      for (let k = i; k >= 0; k--) {
        const q = this.recs[k]!;
        const dt = r.t - q.t;
        if (q.seg[lane] !== seg || dt > win) break;
        const w = Math.exp(-(dt * dt) / (2 * sigma * sigma));
        sw += w; sp += w * q.catsNow[lane]!.pos;
      }
      for (let k = i + 1; k < this.recs.length; k++) {
        const q = this.recs[k]!;
        const dt = q.t - r.t;
        if (q.seg[lane] !== seg || dt > Math.max(LOOKAHEAD_MS, win)) break;
        const qp = q.catsNow[lane]!.pos;
        if (dt <= LOOKAHEAD_MS) upper = Math.min(upper, qp + devAhead);
        if (dt <= win) {
          const w = Math.exp(-(dt * dt) / (2 * sigma * sigma));
          sw += w; sp += w * qp;
        }
      }
      let d = Math.min(sp / sw, upper);
      if (prevDisp && prevRec && prevRec.seg[lane] === seg) d = Math.max(d, prevDisp[lane]!);
      return Math.min(1, Math.max(0, pos - dev, Math.min(d, pos + devAhead)));
    });
  }

  /** How far displayPos may stray from signalPos at frame i: 0.004, or the total of nearby surges. */
  private allowance(i: number, lane: number): number {
    const r = this.recs[i]!;
    const seg = r.seg[lane]!;
    let surge = 0;
    const step = (k: number) => {
      const q = this.recs[k]!, p = this.recs[k - 1];
      if (!p || p.seg[lane] !== seg || q.seg[lane] !== seg) return;
      const d = q.catsNow[lane]!.pos - p.catsNow[lane]!.pos;
      if (d > SURGE_STEP) surge += d;
    };
    for (let k = i; k > 0 && r.t - this.recs[k]!.t <= SURGE_WINDOW_MS && this.recs[k]!.seg[lane] === seg; k--) step(k);
    for (let k = i + 1; k < this.recs.length && this.recs[k]!.t - r.t <= SURGE_WINDOW_MS && this.recs[k]!.seg[lane] === seg; k++) step(k);
    return Math.max(MAX_DEVIATION, Math.min(SURGE_MAX, surge));
  }

  private trim(): void {
    const cutoff = this.newestT() - this.keepMs;
    let n = 0;
    while (n < this.recs.length - 2 && this.recs[n + 1]!.t < cutoff) n++;
    if (n > 0) {
      this.recs.splice(0, n);
      this.finalized = Math.max(0, this.finalized - n);
    }
    const evCut = cutoff - this.keepMs;
    let m = 0;
    while (m < this.events.length && this.events[m]!.t < evCut) m++;
    if (m) this.events.splice(0, m);
    this.gaps = this.gaps.filter((g) => g.to >= cutoff);
  }
}

export function createTimeline(opts: TimelineOptions): Timeline {
  return new Timeline(opts);
}
